import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'

import { CrmChildListTab } from '@/Containers/CrmSection/Components/ChildList'
import { CrmDocumentsTab } from '@/Containers/CrmSection/Components/Documents'
import { CrmHistoryTab } from '@/Containers/CrmSection/Components/History'
import { DictionaryStatusTag, TRANSPORT_LETTERS_TAB_KEY, transportLettersTab } from '@/Containers/TransportationSection/Components'
import { TransportOffersBlock } from '@/Containers/TransportationSection/OfferContainer/Hooks'
import { Form, FormField } from '@/Core/UI/Form'
import { CostCurrencyField } from '@/Ship/Components/CostCurrencyField'
import {
    EntityBody,
    EntityLayout,
    EntityPanel,
    EntityTabs,
    FieldCell,
    FieldGrid,
    FieldSection,
    FileFieldCell,
    SectionEmpty,
} from '@/Ship/Components/EntityCard'
import { Hint } from '@/Ship/Components/Hint'
import { formatDateUk } from '@/Ship/Helpers/fieldViews'
import { formatMoneyWithCurrency } from '@/Ship/Helpers/formatNumber'
import { CheckGlobalResourceAccessTask } from '@/Ship/Tasks/CheckGlobalResourceAccessTask'

import {
    ApplicationCardHeader,
    ApplicationLinkViewCell,
    ApplicationProfitCell,
    ApplicationSourceCell,
    ApplicationStatusCell,
} from '../Components/ApplicationCells'
import { buildApplicationHistoryEntries } from '../Components/applicationHistoryEntries'
import { ApplicationStatusBar } from '../Components/ApplicationStatusBar'
import { APPLICATION_FIELD_LABELS as L } from '../Contracts/ApplicationFieldLabels'
import { APPLICATION_HISTORY_EVENT_MAP } from '../Contracts/ApplicationHistoryEvent'
import {
    APPLICATION_DOCUMENT_RESOURCE,
    APPLICATION_DOCUMENT_REST_URL,
    APPLICATION_PAYMENT_RESOURCE,
    APPLICATION_PAYMENT_REST_URL,
    APPLICATION_REST_URL,
    APPLICATION_STATUS_CODES,
    APPLICATION_STATUS_RESOURCE,
} from '../Contracts/IApplication'
import { ApplyApplicationCreateDefaultsTask } from '../Tasks/ApplyApplicationCreateDefaultsTask'

import type { CrmChildRow } from '@/Containers/CrmSection/Components/ChildList'
import type { ApplicationCrudContextLike, ApplicationFormContextLike } from '../Contracts/IApplication'
import type { TableColumnsType } from 'antd'
import type { ReactNode } from 'react'

interface Props {
    crudContext: ApplicationCrudContextLike | undefined
    formContext: ApplicationFormContextLike | undefined
}

const STATUS = 'status'
const CLIENT = 'client'
const CARRIER = 'carrier'
const FILES = 'files'
const PROFIT = 'amountProfitUAH'

/** Пари «вартість + валюта» — одне поле з валютою (`CostCurrencyField`). */
const COSTS: Record<string, { currency: string; label: string }> = {
    costClient: { currency: 'costClientCurrency', label: L.costClient },
    costToBorder: { currency: 'costToBorderCurrency', label: L.costToBorder },
    costCarrier: { currency: 'costCarrierCurrency', label: L.costCarrier },
}

/**
 * Блоки «Загального» (`SPEC-021` §8.5), склад і порядок полів — легасі
 * (`ApplicationFormTemplate.jsx:19-169`):
 *
 *  - **Загальне** — назва, статус, клієнт, маршрут, вантаж, тип авто, дата
 *    завантаження й «Додаткова інформація» на всю ширину;
 *  - **Заявка клієнту** — поля лівої колонки легасі після опису (назва блоку
 *    нова — пара до «Заявка перевізнику», Q21.3 дефолт), з файлами й прибутком;
 *  - **Відповідальні**, **Заявка перевізнику** — як у легасі;
 *  - **Інше** — лише перегляд: джерело, програш, проблема, статус для клієнта;
 *  - **Службова інформація** — останньою.
 */
const SECTIONS = {
    general: [
        'title',
        STATUS,
        CLIENT,
        'isConfirmedClient',
        'loadingCountries',
        'loadingAddresses',
        'unloadingCountries',
        'unloadingAddresses',
        'cargo',
        'typesCars',
        'dateLoading',
        'description',
    ],
    client: [
        'planneDateUnloading',
        'forwarder1',
        'costClient',
        'placeCountryDeparture',
        'personResponsibleLoading',
        'placeDestination',
        'personResponsibleUnloading',
        'costCargoInvoice',
        'dateUnloading',
    ],
    responsibles: ['manager', 'responsible', 'dateAdditionResponsible'],
    carrier: [
        'forwarder2',
        CARRIER,
        'numberVehicle',
        'driver',
        'borderCrossing',
        'costToBorder',
        'typeNumberVehicles',
        'method',
        'costCarrier',
        'paymentTerm',
        'dateDeliveryCustoms',
        'consignor',
        'consignee',
        'quantityWeightCargo',
    ],
    other: [
        'clientContract',
        'carrierContract',
        'request',
        'tender',
        'lossReason',
        'lossComment',
        'problem',
        'solvedProblem',
        'statusClient',
        'dateUpdateStatusClient',
    ],
    service: ['createdAt', 'createdBy', 'updatedAt', 'updatedBy'],
} as const

/** На всю ширину: довгі тексти й переліки. */
const WIDE = new Set<string>(['cargo', 'description', 'quantityWeightCargo', 'lossComment', 'problem', 'solvedProblem'])

/** Ставить сервер — у формі нової заявки їм нема чого показати. */
const SERVER_FIELDS = new Set<string>([
    'title',
    STATUS,
    'dateAdditionResponsible',
    'lossReason',
    'lossComment',
    'problem',
    'solvedProblem',
    'statusClient',
    'dateUpdateStatusClient',
    ...SECTIONS.service,
])

/** Поля «Інше», які показуються лише заповненими (порожня проблема — не проблема). */
const SHOWN_WHEN_FILLED = new Set<string>(['lossReason', 'lossComment', 'problem', 'solvedProblem', 'request', 'tender'])

/** Розставлене по блоках; «Ід» — шапка, прапорці проблеми — у «Опис проблеми». */
const PLACED = new Set<string>([
    ...Object.values(SECTIONS).flat(),
    ...Object.values(COSTS).map(({ currency }) => currency),
    FILES,
    PROFIT,
    'id',
    'isProblem',
    'isSolvedProblem',
])

/** Значення за замовчуванням форми створення — один раз на відкриту форму. */
const ApplicationCreateDefaults = ({ formContext }: { formContext: ApplicationFormContextLike }) => {
    useEffect(() => {
        void ApplyApplicationCreateDefaultsTask(formContext)
    }, [formContext])

    return null
}

/**
 * Чи показувати поле «Інше», яке має сенс лише заповненим. Окремий observer:
 * читає значення, а шаблон значень не читає (`SPEC-002` §4.2).
 */
const FilledOnly = observer(
    ({ formContext, name, children }: { formContext: ApplicationFormContextLike; name: string; children: ReactNode }) => {
        const value = formContext.values?.[name]

        if (value === undefined || value === null || value === '' || value === false) return null

        return <>{children}</>
    }
)

/** Рядок оплати так, як його віддає колекція `application_payments`. */
interface ApplicationPaymentRow extends CrmChildRow {
    date?: string | null
    sum?: number | null
    sumCurrency?: { code?: string } | null
    sumBaseCurrency?: number | null
    comment?: string | null
}

/** Колонки «Оплат» — як у легасі (`ApplicationPaymentCrud.jsx`), без фільтрів. */
const PAYMENT_COLUMNS: TableColumnsType<ApplicationPaymentRow> = [
    { title: 'Дата оплати', key: 'date', render: (_: unknown, row) => (row.date ? formatDateUk(row.date) : '—') },
    {
        title: 'Сума оплати',
        key: 'sum',
        render: (_: unknown, row) => formatMoneyWithCurrency(row.sum ?? null, row.sumCurrency?.code ?? 'EUR'),
    },
    {
        title: 'Сума транзакції в базовій валюті',
        key: 'sumBaseCurrency',
        render: (_: unknown, row) => (typeof row.sumBaseCurrency === 'number' ? formatMoneyWithCurrency(row.sumBaseCurrency, 'UAH') : '—'),
    },
    { title: 'Коментар', key: 'comment', render: (_: unknown, row) => row.comment || '—' },
]

/**
 * Підказка біля «Прибуток» (`SPEC-021` §11): статус — бейджем із довідника,
 * а не назвою в лапках (вартовий підказок).
 */
const ProfitHint = () => (
    <Hint id="transport.application.profit">
        Прибуток рахується в гривні при переході в{' '}
        <DictionaryStatusTag resource={APPLICATION_STATUS_RESOURCE} code={APPLICATION_STATUS_CODES.documentManagement} /> за
        курсом НБУ на дату переходу; зміна вартостей після цього перераховує його за тим самим курсом.
    </Hint>
)

/**
 * Картка заявки на спільних правилах карток (`EntityLayout`, як картка
 * клієнта): шапка «Заявка: <клієнт>» зі статусом і «Ід», смуга статусів,
 * блоки полів двома стовпцями; вкладки — лише в збереженої (`SPEC-021` §8.4).
 *
 * Праворуч — блок «Цінові пропозиції» (Hook `OfferContainer`; у формі
 * створення — пояснення, коли їх можна буде додати). Вкладка «Листи» —
 * Н-025.2, генерація документів — Н-025.1.
 *
 * ⚠️ Значень полів, які редагують, шаблон не читає — лише `uuid`: інакше
 * ядровий `FormField` перемонтовував би всі поля на кожну літеру. Живі
 * значення показують observer-компоненти.
 */
export const ApplicationFormTemplate = observer(({ crudContext, formContext }: Props) => {
    const fields = crudContext?.resource?.fields ?? {}
    const uuid = formContext?.values?.uuid
    const isNew = typeof uuid !== 'string' || uuid === ''
    const isEditable = formContext?.isEditable === true

    const [documentsEpoch, setDocumentsEpoch] = useState(0)
    const [paymentsEpoch, setPaymentsEpoch] = useState(0)
    const [historyEpoch, setHistoryEpoch] = useState(0)
    const [lettersEpoch, setLettersEpoch] = useState(0)

    // Поля, додані в ресурс пізніше, не мають зникати мовчки
    const rest = Object.keys(fields).filter((name) => !PLACED.has(name))

    const cell = (name: string): ReactNode => {
        if (isNew && SERVER_FIELDS.has(name)) return null

        if (!formContext) return null

        if (name === STATUS) {
            return (
                <FieldCell key={name}>
                    <ApplicationStatusCell formContext={formContext} />
                </FieldCell>
            )
        }

        if ((name === CLIENT || name === CARRIER) && !isEditable) {
            return (
                <FieldCell key={name}>
                    <ApplicationLinkViewCell formContext={formContext} name={name} kind={name} />
                </FieldCell>
            )
        }

        if (name === 'request' || name === 'tender') {
            return (
                <FilledOnly key={name} formContext={formContext} name={name}>
                    <FieldCell>
                        <ApplicationSourceCell formContext={formContext} name={name} />
                    </FieldCell>
                </FilledOnly>
            )
        }

        const cost = COSTS[name]

        if (cost) {
            return (
                <FieldCell key={name}>
                    <CostCurrencyField formContext={formContext} amount={name} currency={cost.currency} label={cost.label} />
                </FieldCell>
            )
        }

        if (!(name in fields)) return null

        const field = (
            <FieldCell key={name} wide={WIDE.has(name)}>
                <FormField name={name} />
            </FieldCell>
        )

        return SHOWN_WHEN_FILLED.has(name) ? (
            <FilledOnly key={name} formContext={formContext} name={name}>
                {field}
            </FilledOnly>
        ) : (
            field
        )
    }

    const grid = (names: readonly string[], extra?: ReactNode) => (
        <Form formContext={formContext}>
            <FieldGrid adaptive>
                {names.map(cell)}
                {extra}
            </FieldGrid>
        </Form>
    )

    const clientExtra = formContext ? (
        <>
            <FieldCell wide>
                <FileFieldCell formContext={formContext} name={FILES} label={L.files} />
            </FieldCell>
            {isNew ? null : (
                <FieldCell wide>
                    <ProfitHint />
                    <ApplicationProfitCell formContext={formContext} />
                </FieldCell>
            )}
        </>
    ) : null

    // Блок пропозицій — Hook `OfferContainer`; у формі створення — пояснення.
    // Картка заявки — на всю ширину (рішення замовника 2026-10-22): смуга
    // статусів, пропозиції й поля не ділять екран зі стрічкою праворуч.
    const offers = isNew ? (
        <FieldSection title="Цінові пропозиції">
            <SectionEmpty>Пропозиції можна буде додати після збереження заявки.</SectionEmpty>
        </FieldSection>
    ) : (
        <TransportOffersBlock kind="application" owner={formContext} />
    )

    const main = (
        <>
            {formContext && crudContext && !isNew ? <ApplicationStatusBar crudContext={crudContext} formContext={formContext} /> : null}
            <FieldSection title="Загальне">{grid(SECTIONS.general)}</FieldSection>
            {offers}
            <FieldSection title="Заявка клієнту">{grid(SECTIONS.client, clientExtra)}</FieldSection>
            <FieldSection title="Відповідальні">{grid(SECTIONS.responsibles)}</FieldSection>
            <FieldSection title="Заявка перевізнику">{grid(SECTIONS.carrier)}</FieldSection>
            <FieldSection title="Інше">{grid(SECTIONS.other)}</FieldSection>
            <FieldSection title="Додатково" hidden={rest.length === 0}>
                {grid(rest)}
            </FieldSection>
            <FieldSection title="Службова інформація" hidden={isNew}>
                {grid(SECTIONS.service)}
            </FieldSection>
        </>
    )

    if (isNew || !formContext) {
        return (
            <EntityLayout>
                {formContext ? <ApplicationCreateDefaults formContext={formContext} /> : null}
                <EntityBody main={main} />
            </EntityLayout>
        )
    }

    const canAddPayment = CheckGlobalResourceAccessTask(APPLICATION_PAYMENT_RESOURCE, 'create')

    return (
        <EntityLayout>
            <EntityTabs
                header={<ApplicationCardHeader formContext={formContext} />}
                onChange={(key) => {
                    if (key === 'documents') setDocumentsEpoch((epoch) => epoch + 1)
                    if (key === 'payments') setPaymentsEpoch((epoch) => epoch + 1)
                    if (key === 'history') setHistoryEpoch((epoch) => epoch + 1)
                    if (key === TRANSPORT_LETTERS_TAB_KEY) setLettersEpoch((epoch) => epoch + 1)
                }}
                items={[
                    { key: 'general', label: 'Загальне', children: <EntityBody main={main} /> },
                    {
                        key: 'documents',
                        label: 'Друковані форми',
                        children: (
                            <EntityPanel>
                                <CrmDocumentsTab
                                    key={documentsEpoch}
                                    resource={APPLICATION_DOCUMENT_RESOURCE}
                                    restUrl={APPLICATION_DOCUMENT_REST_URL}
                                    ownerField="application"
                                    ownerFormContext={formContext}
                                />
                            </EntityPanel>
                        ),
                    },
                    {
                        key: 'payments',
                        label: 'Оплати',
                        children: (
                            <EntityPanel>
                                <CrmChildListTab<ApplicationPaymentRow>
                                    key={paymentsEpoch}
                                    resource={APPLICATION_PAYMENT_RESOURCE}
                                    restUrl={APPLICATION_PAYMENT_REST_URL}
                                    ownerField="application"
                                    ownerFormContext={formContext}
                                    canAdd={canAddPayment}
                                    columns={PAYMENT_COLUMNS}
                                    texts={{
                                        add: 'Додати оплату',
                                        empty: 'Оплат ще немає.',
                                        loadFailed: 'Не вдалося завантажити оплати.',
                                        removeTitle: 'Видалити оплату?',
                                        removeDescription: 'Оплата зникне зі списку, відмітка про оплату заявки перерахується.',
                                        removeLabel: 'Видалити оплату',
                                        countNoun: 'оплат',
                                    }}
                                />
                            </EntityPanel>
                        ),
                    },
                    ...transportLettersTab('application', uuid, lettersEpoch),
                    {
                        key: 'history',
                        label: 'Історія змін',
                        children: (
                            <EntityPanel>
                                <CrmHistoryTab
                                    key={historyEpoch}
                                    restUrl={APPLICATION_REST_URL}
                                    uuid={uuid}
                                    events={APPLICATION_HISTORY_EVENT_MAP}
                                    buildEntries={buildApplicationHistoryEntries}
                                />
                            </EntityPanel>
                        ),
                    },
                ]}
            />
        </EntityLayout>
    )
})
